import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext';
import toast from 'react-hot-toast';
import {
  ArrowLeftIcon,
  DocumentArrowDownIcon,
  ChartBarIcon,
  CalendarIcon,
  CurrencyYenIcon,
  UserGroupIcon,
  StarIcon,
  ClockIcon,
  FunnelIcon
} from '@heroicons/react/24/outline';
import { API_BASE } from '../../lib/config';

const API = `${API_BASE}`;

const EmployerReports = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('analytics');
  const [loading, setLoading] = useState(false);
  // 日付ユーティリティ
  const fmt = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };
  const todayStr = fmt(new Date());
  const monthStartStr = (() => {
    const now = new Date();
    return fmt(new Date(now.getFullYear(), now.getMonth(), 1));
  })();

  const [dateRange, setDateRange] = useState({
    start: monthStartStr,
    end: todayStr
  });
  
  // レポートデータ（本番/byters.jpではゼロ＆空配列を初期値に）
  const isProdSite = typeof window !== 'undefined' && /byters\.jp$/.test(window.location.hostname);
  const zeroAnalytics = {
    summary: { total_jobs: 0, total_workers: 0, total_cost: 0, average_rating: 0, completion_rate: 0 },
    monthly_data: [],
    category_breakdown: []
  };

  const fetchEmployerPayments = async () => {
    const token = localStorage.getItem('token');
    const endpoints = [
      `${API}/employers/payments/list`,
      `${API}/employers/payments`,
      `${API}/employer/payments/list`,
      `${API}/employer/payments`
    ];
    try {
      setLoading(true);
      let data = [];
      for (const url of endpoints) {
        try {
          const res = await fetch(url, { headers: { 'Authorization': `Bearer ${token}` } });
          if (res.ok) {
            const json = await res.json();
            if (Array.isArray(json)) { data = json; break; }
          }
        } catch (_) { /* try next */ }
      }
      setPayments(Array.isArray(data) ? data : []);
    } catch (e) {
      setPayments([]);
    } finally {
      setLoading(false);
    }
  };
  const [analyticsData, setAnalyticsData] = useState(
    (process.env.NODE_ENV === 'production' || isProdSite) ? zeroAnalytics : zeroAnalytics
  );
  
  const [reviewsData, setReviewsData] = useState([]);
  const [payments, setPayments] = useState([]);

  useEffect(() => {
    if (!user || user.user_type !== 'employer') {
      toast.error('事業者権限が必要です');
      navigate('/');
      return;
    }
    
    // 本番/byters.jpではデモ表示禁止し、決済履歴のみ表示
    if (process.env.NODE_ENV === 'production' || isProdSite) {
      setAnalyticsData(zeroAnalytics);
      setReviewsData([]);
      fetchEmployerPayments();
    } else {
      fetchReportsData();
    }
  }, [user, navigate, dateRange]);

  const fetchReportsData = async () => {
    try {
      setLoading(true);
      // 実際の実装では期間フィルタでデータを取得
      // API呼び出しを実装
    } catch (err) {
      console.error('Error fetching reports data:', err);
      toast.error('レポートデータの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const handleCSVDownload = async (type) => {
    try {
      let csvData = '';
      let filename = '';
      
      switch (type) {
        case 'jobs':
          csvData = generateJobsCSV();
          filename = `求人履歴_${dateRange.start}_${dateRange.end}.csv`;
          break;
        case 'workers':
          csvData = generateWorkersCSV();
          filename = `ワーカー一覧_${dateRange.start}_${dateRange.end}.csv`;
          break;
        case 'payments':
          csvData = generatePaymentsCSV();
          filename = `支払い履歴_${dateRange.start}_${dateRange.end}.csv`;
          break;
        case 'reviews':
          csvData = generateReviewsCSV();
          filename = `評価履歴_${dateRange.start}_${dateRange.end}.csv`;
          break;
        default:
          return;
      }
      
      downloadCSV(csvData, filename);
      toast.success('CSVファイルをダウンロードしました');
    } catch (err) {
      console.error('Error downloading CSV:', err);
      toast.error('CSVダウンロードに失敗しました');
    }
  };

  const generateJobsCSV = () => {
    const headers = ['求人ID', '求人タイトル', 'カテゴリ', '勤務日', '時給', '募集人数', '応募者数', '作成日'];
    const data = analyticsData.monthly_data.map(month => [
      `job-${month.month}`,
      'サンプル求人',
      '飲食・フード',
      month.month,
      '1200',
      '2',
      month.workers,
      month.month
    ]);
    
    return [headers, ...data].map(row => row.join(',')).join('\n');
  };

  const generateWorkersCSV = () => {
    const headers = ['ワーカー名', '勤務日', '求人タイトル', '勤務時間', '報酬', '評価'];
    const data = reviewsData.map(review => [
      review.worker_name,
      review.work_date,
      review.job_title,
      '5時間',
      '6500円',
      review.rating
    ]);
    
    return [headers, ...data].map(row => row.join(',')).join('\n');
  };

  const generatePaymentsCSV = () => {
    const headers = ['支払日', 'ワーカー名', '求人タイトル', '報酬額', '手数料', '総支払額'];
    const data = reviewsData.map(review => [
      review.created_at,
      review.worker_name,
      review.job_title,
      '6500円',
      '650円',
      '7150円'
    ]);
    
    return [headers, ...data].map(row => row.join(',')).join('\n');
  };

  const generateReviewsCSV = () => {
    const headers = ['評価日', 'ワーカー名', '求人タイトル', '評価', 'コメント'];
    const data = reviewsData.map(review => [
      review.created_at,
      review.worker_name,
      review.job_title,
      review.rating,
      `"${review.comment}"`
    ]);
    
    return [headers, ...data].map(row => row.join(',')).join('\n');
  };

  const downloadCSV = (csvData, filename) => {
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvData], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const renderAnalytics = () => (
    <div className="space-y-6">
      {/* サマリーカード */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <CalendarIcon className="w-6 h-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">総求人数</p>
              <p className="text-2xl font-bold text-gray-900">{analyticsData.summary.total_jobs}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <UserGroupIcon className="w-6 h-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">総ワーカー数</p>
              <p className="text-2xl font-bold text-gray-900">{analyticsData.summary.total_workers}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <CurrencyYenIcon className="w-6 h-6 text-yellow-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">総コスト</p>
              <p className="text-2xl font-bold text-gray-900">{analyticsData.summary.total_cost.toLocaleString()}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="p-2 bg-purple-100 rounded-lg">
              <StarIcon className="w-6 h-6 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">平均評価</p>
              <p className="text-2xl font-bold text-gray-900">{analyticsData.summary.average_rating}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="p-2 bg-indigo-100 rounded-lg">
              <ChartBarIcon className="w-6 h-6 text-indigo-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">完了率</p>
              <p className="text-2xl font-bold text-gray-900">{analyticsData.summary.completion_rate}%</p>
            </div>
          </div>
        </div>
      </div>

      {/* 月別データ */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">月別実績</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">月</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">求人数</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ワーカー数</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">コスト</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">平均評価</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {analyticsData.monthly_data.map((month) => (
                <tr key={month.month}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {new Date(month.month).toLocaleDateString('ja-JP', { year: 'numeric', month: 'long' })}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{month.jobs}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{month.workers}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{month.cost.toLocaleString()}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <div className="flex items-center">
                      <StarIcon className="w-4 h-4 text-yellow-400 mr-1" />
                      {month.avg_rating}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* カテゴリ別内訳 */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">カテゴリ別内訳</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {analyticsData.category_breakdown.map((category) => (
            <div key={category.category} className="border border-gray-200 rounded-lg p-4">
              <h4 className="font-medium text-gray-900 mb-3">{category.category}</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">求人数:</span>
                  <span className="font-medium">{category.jobs}件</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">ワーカー数:</span>
                  <span className="font-medium">{category.workers}人</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">コスト:</span>
                  <span className="font-medium">{category.cost.toLocaleString()}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderCSVDownloads = () => (
    <div className="bg-white rounded-lg shadow p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">データエクスポート</h3>
      <p className="text-gray-600 mb-6">
        過去の案件データをCSVファイルでダウンロードできます。指定期間のデータが出力されます。
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <button
          onClick={() => handleCSVDownload('jobs')}
          className="flex items-center justify-center p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-colors"
        >
          <div className="text-center">
            <DocumentArrowDownIcon className="w-8 h-8 text-gray-400 mx-auto mb-2" />
            <span className="font-medium text-gray-900">求人履歴データ</span>
            <p className="text-sm text-gray-600 mt-1">求人情報、応募状況、作成日など</p>
          </div>
        </button>

        <button
          onClick={() => handleCSVDownload('workers')}
          className="flex items-center justify-center p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-colors"
        >
          <div className="text-center">
            <DocumentArrowDownIcon className="w-8 h-8 text-gray-400 mx-auto mb-2" />
            <span className="font-medium text-gray-900">ワーカー履歴データ</span>
            <p className="text-sm text-gray-600 mt-1">ワーカー情報、勤務実績、評価など</p>
          </div>
        </button>

        <button
          onClick={() => handleCSVDownload('payments')}
          className="flex items-center justify-center p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-colors"
        >
          <div className="text-center">
            <DocumentArrowDownIcon className="w-8 h-8 text-gray-400 mx-auto mb-2" />
            <span className="font-medium text-gray-900">支払い履歴データ</span>
            <p className="text-sm text-gray-600 mt-1">報酬支払い、手数料、決済情報など</p>
          </div>
        </button>

        <button
          onClick={() => handleCSVDownload('reviews')}
          className="flex items-center justify-center p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-colors"
        >
          <div className="text-center">
            <DocumentArrowDownIcon className="w-8 h-8 text-gray-400 mx-auto mb-2" />
            <span className="font-medium text-gray-900">レビュー履歴データ</span>
            <p className="text-sm text-gray-600 mt-1">ワーカー評価、コメント、評価日など</p>
          </div>
        </button>
      </div>
    </div>
  );

  const renderReviews = () => (
    <div className="bg-white rounded-lg shadow">
      <div className="p-6 border-b border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900">レビュー履歴</h3>
        <p className="text-sm text-gray-600 mt-1">過去に投稿したワーカーへの評価とコメント</p>
      </div>

      <div className="divide-y divide-gray-200">
        {reviewsData.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            レビュー履歴がありません
          </div>
        ) : (
          reviewsData.map((review) => (
            <div key={review.id} className="p-6">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h4 className="font-medium text-gray-900">{review.worker_name}</h4>
                  <p className="text-sm text-gray-600 mt-1">
                    {review.job_title} - {review.work_date}
                  </p>
                </div>
                <div className="flex items-center">
                  <div className="flex mr-2">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <StarIcon
                        key={star}
                        className={`w-4 h-4 ${
                          star <= review.rating ? 'text-yellow-400 fill-current' : 'text-gray-300'
                        }`}
                      />
                    ))}
                  </div>
                  <span className="text-sm text-gray-500">{review.created_at}</span>
                </div>
              </div>
              
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-700">{review.comment}</p>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );

  // 本番/byters.jpは常に「決済履歴のみ」を表示
  if (process.env.NODE_ENV === 'production' || isProdSite) {
    const startDate = dateRange.start ? new Date(`${dateRange.start}T00:00:00`) : null;
    const endDate = dateRange.end ? new Date(`${dateRange.end}T23:59:59`) : null;
    const filteredPayments = payments.filter(p => {
      try {
        const d = new Date(p.created_at || p.createdAt || p.created || 0);
        if (isNaN(d.getTime())) return true;
        if (startDate && d < startDate) return false;
        if (endDate && d > endDate) return false;
        return true;
      } catch (_) { return true; }
    });
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="container mx-auto px-4 py-8">
          <div className="mb-8">
            <button onClick={() => navigate('/employer-dashboard')} className="flex items-center text-gray-600 hover:text-gray-900 mb-4">
              <ArrowLeftIcon className="w-5 h-5 mr-2" />
              事業者ダッシュボードに戻る
            </button>
            <h1 className="text-3xl font-bold text-gray-900">決済履歴</h1>
            <p className="text-gray-600 mt-2">実際の支払いのみが表示されます</p>
          </div>

          {/* 期間フィルター（本番でも表示） */}
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <div className="flex items-center space-x-4">
              <div className="flex items-center">
                <FunnelIcon className="w-5 h-5 text-gray-400 mr-2" />
                <span className="text-sm font-medium text-gray-700">期間指定:</span>
              </div>
              <input
                type="date"
                value={dateRange.start}
                onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <span className="text-gray-500">〜</span>
              <input
                type="date"
                value={dateRange.end}
                onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            {loading ? (
              <div className="py-12 flex justify-center items-center">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500"></div>
              </div>
            ) : filteredPayments.length === 0 ? (
              <div className="text-center text-gray-500 py-12">
                決済履歴がありません
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">日付</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">求職者受取</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">運営手数料</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">総支払額</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ステータス</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredPayments.map(p => (
                      <tr key={p.id}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{(p.created_at || '').toString().replace('T', ' ').slice(0, 16)}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{Number(p.job_seeker_amount || 0).toLocaleString()}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{Number(p.platform_fee || 0).toLocaleString()}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{Number(p.employer_total_payment || 0).toLocaleString()}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{p.status || 'completed'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // 非本番は従来の分析・CSV・レビュータブを表示
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex justify-center items-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-8">
        {/* ヘッダー */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/employer-dashboard')}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeftIcon className="w-5 h-5 mr-2" />
            事業者ダッシュボードに戻る
          </button>
          <h1 className="text-3xl font-bold text-gray-900">絞り込み・レポート機能</h1>
          <p className="text-gray-600 mt-2">CSVダウンロード・採用コスト集計・レビュー履歴</p>
        </div>

        {/* 期間フィルター */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="flex items-center space-x-4">
            <div className="flex items-center">
              <FunnelIcon className="w-5 h-5 text-gray-400 mr-2" />
              <span className="text-sm font-medium text-gray-700">期間指定:</span>
            </div>
            <input
              type="date"
              value={dateRange.start}
              onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
              onFocus={() => setDateRange(prev => ({ ...prev, start: monthStartStr }))}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <span className="text-gray-500">〜</span>
            <input
              type="date"
              value={dateRange.end}
              onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
              onFocus={() => setDateRange(prev => ({ ...prev, end: todayStr }))}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>

        {/* タブナビゲーション */}
        <div className="bg-white rounded-lg shadow mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6">
              <button
                onClick={() => setActiveTab('analytics')}
                className={`flex items-center py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'analytics'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <ChartBarIcon className="w-5 h-5 mr-2" />
                分析・集計
              </button>
              <button
                onClick={() => setActiveTab('csv')}
                className={`flex items-center py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'csv'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <DocumentArrowDownIcon className="w-5 h-5 mr-2" />
                CSVダウンロード
              </button>
              <button
                onClick={() => setActiveTab('reviews')}
                className={`flex items-center py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'reviews'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <StarIcon className="w-5 h-5 mr-2" />
                レビュー履歴
              </button>
            </nav>
          </div>
        </div>

        {/* タブコンテンツ */}
        {activeTab === 'analytics' && renderAnalytics()}
        {activeTab === 'csv' && renderCSVDownloads()}
        {activeTab === 'reviews' && renderReviews()}
      </div>
    </div>
  );
};

export default EmployerReports;